import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDialog } from '@spotify/confidence-core-react';

import { CodingSessionDialog } from '../../components/CodingSessions/CodingSessionDialog';
import { useChatRef } from '../useChatRef';

// Query parameter carrying the child chat name of a coding session to open. A
// clickable project-level context entry navigates to its parent chat with this
// param; the chat page opens the session transcript and strips it.
export const CODING_SESSION_DEEP_LINK_PARAM = 'codingSession';

const DeepLinkedCodingSessionDialog = ({
  childChatName,
  onClose,
}: {
  childChatName: string;
  onClose: () => void;
}) => {
  const { chat } = useChatRef({ name: childChatName });
  return (
    <CodingSessionDialog
      childChatName={childChatName}
      title={chat?.displayName ?? 'Coding session'}
      onClose={onClose}
    />
  );
};

// Opens the coding-session transcript dialog when the current URL carries the
// deep-link param, then strips the param so a refresh or back navigation does
// not reopen it. Disabled on a page that is about to redirect (e.g. a standalone
// chat that belongs to a project) so the param survives the redirect and the
// destination page opens the dialog instead.
export const useCodingSessionDeepLink = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { openDialog, closeDialog } = useDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  // Tracks the child chat name last opened so each distinct deep link is handled
  // once. Reset when the param clears so the same session can be reopened later.
  const handledRef = useRef<string | null>(null);

  const childChatName = searchParams.get(CODING_SESSION_DEEP_LINK_PARAM);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!childChatName) {
      handledRef.current = null;
      return;
    }
    if (handledRef.current === childChatName) {
      return;
    }
    handledRef.current = childChatName;

    setSearchParams(
      current => {
        const next = new URLSearchParams(current);
        next.delete(CODING_SESSION_DEEP_LINK_PARAM);
        return next;
      },
      { replace: true },
    );

    openDialog({
      content: (
        <DeepLinkedCodingSessionDialog
          childChatName={childChatName}
          onClose={closeDialog}
        />
      ),
      dialogProps: { maxWidth: 'md' },
      onClose: closeDialog,
    });
  }, [enabled, childChatName, openDialog, closeDialog, setSearchParams]);
};
